import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/services/supabase/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
}

interface AuthContextValue extends AuthState {
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: AuthError | null; requiresEmailConfirmation: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    initialized: false,
  });

  const supabase = getSupabaseClient();

  const refreshSession = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      setState(prev => ({
        ...prev,
        session: data.session,
        user: data.session?.user ?? null,
      }));
    } catch {
      // session refresh failure is non-fatal
    }
  }, [supabase]);

  // Auth state listener — sync only (no async/await)
  useEffect(() => {
    let mounted = true;

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;

        setState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          loading: false,
          initialized: true,
        }));
      },
    );

    // Initial session fetch — non-blocking
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setState(prev => ({
        ...prev,
        session: data.session,
        user: data.session?.user ?? null,
        loading: false,
        initialized: true,
      }));
    });

    return () => {
      mounted = false;
      subscription?.subscription?.unsubscribe();
    };
  }, [supabase]);

  const signUp = useCallback(
    async (email: string, password: string, displayName?: string) => {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: displayName ? { display_name: displayName } : undefined,
          },
        });
        return {
          error,
          requiresEmailConfirmation: data?.user?.identities?.length === 0 || false,
        };
      } catch (err: unknown) {
        return { error: err as AuthError, requiresEmailConfirmation: false };
      }
    },
    [supabase],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
      } catch (err: unknown) {
        return { error: err as AuthError };
      }
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (err: unknown) {
      return { error: err as AuthError };
    }
  }, [supabase]);

  const resetPassword = useCallback(
    async (email: string) => {
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        return { error };
      } catch (err: unknown) {
        return { error: err as AuthError };
      }
    },
    [supabase],
  );

  const updatePassword = useCallback(
    async (newPassword: string) => {
      try {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        return { error };
      } catch (err: unknown) {
        return { error: err as AuthError };
      }
    },
    [supabase],
  );

  const value: AuthContextValue = {
    ...state,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}